import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import Sidebar from '../../components/navigation/Sidebar';
import PatientContextBar from '../../components/navigation/PatientContextBar';
import QuickActions from '../../components/navigation/QuickActions';
import SessionManager from '../../components/navigation/SessionManager';
import TreatmentPlanDetails from './components/TreatmentPlanDetails';
import ConsentWorkflow from './components/ConsentWorkflow';
import DigitalSignature from './components/DigitalSignature';
import TreatmentComparison from './components/TreatmentComparison';
import DiscussionPanel from './components/DiscussionPanel';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { consentService } from '../../services/consentService';
import { useAuth } from '../../contexts/AuthContext';

const TreatmentPlanReview = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const [isSidebarCollapsed, setIsSidebarCollapsed] = useState(false);
  const [activeTab, setActiveTab] = useState('details');
  const [treatmentPlan, setTreatmentPlan] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [showSignature, setShowSignature] = useState(false);
  const [allAcknowledged, setAllAcknowledged] = useState(false);

  const treatmentPlanId = searchParams?.get('id');

  useEffect(() => {
    if (treatmentPlanId) {
      loadTreatmentPlan();
    } else {
      setError('No treatment plan ID provided');
      setLoading(false);
    }
  }, [treatmentPlanId]);

  useEffect(() => {
    if (treatmentPlan?.consentWorkflow?.acknowledgments) {
      const allChecked = treatmentPlan?.consentWorkflow?.acknowledgments?.every(
        ack => ack?.acknowledged
      );
      setAllAcknowledged(allChecked);
    }
  }, [treatmentPlan]);

  const loadTreatmentPlan = async () => {
    try {
      setLoading(true);
      const data = await consentService?.getTreatmentPlanForReview(treatmentPlanId);
      setTreatmentPlan(data);
    } catch (err) {
      setError(err?.message || 'Failed to load treatment plan');
    } finally {
      setLoading(false);
    }
  };

  const handleAcceptPlan = async () => {
    if (!allAcknowledged) {
      alert('Please review and acknowledge all treatment phases before accepting.');
      return;
    }
    setShowSignature(true);
  };

  const handleSignatureComplete = async (signatureData) => {
    try {
      // Save signature
      await consentService?.saveDigitalSignature(
        treatmentPlan?.consentWorkflow?.id,
        signatureData
      );

      // Update consent status
      await consentService?.updateConsentStatus(
        treatmentPlan?.consentWorkflow?.id,
        'accepted',
        {
          review_completed_at: new Date()?.toISOString()
        }
      );

      // Send email notifications
      await consentService?.sendConsentNotification({
        recipientEmail: treatmentPlan?.provider?.email,
        recipientName: treatmentPlan?.provider?.full_name,
        notificationType: 'consent_accepted',
        treatmentPlanName: treatmentPlan?.planName,
        patientName: treatmentPlan?.patient?.full_name,
        providerName: treatmentPlan?.provider?.full_name,
        consentWorkflowId: treatmentPlan?.consentWorkflow?.id
      });

      await consentService?.sendConsentNotification({
        recipientEmail: treatmentPlan?.patient?.email,
        recipientName: treatmentPlan?.patient?.full_name,
        notificationType: 'signature_completed',
        treatmentPlanName: treatmentPlan?.planName,
        patientName: treatmentPlan?.patient?.full_name,
        consentWorkflowId: treatmentPlan?.consentWorkflow?.id
      });

      alert('Treatment plan accepted successfully! Email notifications have been sent.');
      navigate('/patient-portal');
    } catch (err) {
      alert(err?.message || 'Failed to complete consent process');
    }
  };

  const handleRejectPlan = async () => {
    if (!confirm('Are you sure you want to decline this treatment plan?')) return;

    try {
      await consentService?.updateConsentStatus(
        treatmentPlan?.consentWorkflow?.id,
        'rejected',
        {
          review_completed_at: new Date()?.toISOString()
        }
      );

      // Send notification to provider
      await consentService?.sendConsentNotification({
        recipientEmail: treatmentPlan?.provider?.email,
        recipientName: treatmentPlan?.provider?.full_name,
        notificationType: 'consent_rejected',
        treatmentPlanName: treatmentPlan?.planName,
        patientName: treatmentPlan?.patient?.full_name,
        providerName: treatmentPlan?.provider?.full_name,
        consentWorkflowId: treatmentPlan?.consentWorkflow?.id
      });

      alert('Treatment plan declined. Your provider has been notified.');
      navigate('/patient-portal');
    } catch (err) {
      alert(err?.message || 'Failed to decline treatment plan');
    }
  };

  const handleRequestConsultation = async (questions) => {
    try {
      await consentService?.requestConsultation(
        treatmentPlan?.consentWorkflow?.id,
        questions
      );

      // Send notification to provider
      await consentService?.sendConsentNotification({
        recipientEmail: treatmentPlan?.provider?.email,
        recipientName: treatmentPlan?.provider?.full_name,
        notificationType: 'consultation_requested',
        treatmentPlanName: treatmentPlan?.planName,
        patientName: treatmentPlan?.patient?.full_name,
        providerName: treatmentPlan?.provider?.full_name,
        consentWorkflowId: treatmentPlan?.consentWorkflow?.id
      });

      alert('Consultation request sent. Your provider will contact you shortly.');
      loadTreatmentPlan();
    } catch (err) {
      alert(err?.message || 'Failed to request consultation');
    }
  };

  const handleSessionExpired = () => {
    alert('Your session has expired. Please log in again.');
    navigate('/login');
  };

  const handleSessionExtended = () => {
    console.log('Session extended successfully');
  };

  const handleAlertClick = (alertId) => {
    console.log('Alert clicked:', alertId);
  };

  const currentPatient = {
    name: treatmentPlan?.patient?.full_name || 'Patient',
    mrn: 'MRN-2024-789456',
    dob: '03/15/1978',
    age: '48 years'
  };

  const patientAlerts = [
    {
      id: 1,
      severity: 'info',
      title: 'Treatment Plan Review',
      message: 'Please review all treatment phases and provide your consent to proceed.',
      dismissible: false
    }
  ];

  const tabs = [
    { id: 'details', label: 'Treatment Details', icon: 'FileText' },
    { id: 'consent', label: 'Consent Workflow', icon: 'CheckSquare' },
    { id: 'comparison', label: 'Compare Options', icon: 'GitCompare' },
    { id: 'discussion', label: 'Discussion', icon: 'MessageSquare' }
  ];

  if (loading) {
    return (
      <div className="flex h-screen bg-background pt-16">
        <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={setIsSidebarCollapsed} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Icon name="Loader2" size={48} className="animate-spin text-primary mx-auto mb-4" />
            <p className="text-muted-foreground">Loading treatment plan...</p>
          </div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex h-screen bg-background pt-16">
        <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={setIsSidebarCollapsed} />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <Icon name="AlertCircle" size={48} className="text-error mx-auto mb-4" />
            <p className="text-foreground font-semibold mb-2">Error Loading Treatment Plan</p>
            <p className="text-muted-foreground mb-4">{error}</p>
            <Button onClick={() => navigate('/patient-portal')}>Return to Portal</Button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex h-screen bg-background overflow-hidden pt-16">
      <Sidebar isCollapsed={isSidebarCollapsed} onToggleCollapse={setIsSidebarCollapsed} />
      <div className="flex-1 flex flex-col overflow-hidden">
        <SessionManager 
          onSessionExpired={handleSessionExpired}
          onSessionExtended={handleSessionExtended}
        />
        <PatientContextBar 
          patient={currentPatient} 
          alerts={patientAlerts}
          onAlertClick={handleAlertClick}
        />
        
        <div className="flex-1 overflow-auto">
          <div className="container mx-auto p-4 md:p-6 max-w-7xl">
            {/* Header */}
            <div className="mb-6">
              <div className="flex items-center gap-2 text-sm text-muted-foreground mb-2">
                <button onClick={() => navigate('/patient-portal')} className="hover:text-foreground">
                  Patient Portal
                </button>
                <Icon name="ChevronRight" size={16} />
                <span className="text-foreground">Treatment Plan Review</span>
              </div>
              <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
                <div>
                  <h1 className="text-2xl md:text-3xl font-bold text-foreground mb-2">
                    {treatmentPlan?.planName}
                  </h1>
                  <p className="text-muted-foreground">
                    Provider: {treatmentPlan?.provider?.full_name} • Duration: {treatmentPlan?.estimatedDuration}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`inline-flex items-center px-3 py-1 rounded-full text-sm font-medium ${
                    treatmentPlan?.consentWorkflow?.consentStatus === 'accepted' ? 'bg-success/10 text-success' :
                    treatmentPlan?.consentWorkflow?.consentStatus === 'rejected' ? 'bg-error/10 text-error' :
                    treatmentPlan?.consentWorkflow?.consentStatus === 'requires_discussion' ? 'bg-warning/10 text-warning' : 'bg-primary/10 text-primary'
                  }`}>
                    {treatmentPlan?.consentWorkflow?.consentStatus?.replace('_', ' ')?.toUpperCase()}
                  </span>
                </div>
              </div>
            </div>

            {/* Cost Summary Card */}
            <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6 mb-6">
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Total Cost</p>
                  <p className="text-2xl font-bold text-foreground">${treatmentPlan?.totalCost?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Insurance Coverage</p>
                  <p className="text-2xl font-bold text-success">${treatmentPlan?.insuranceCoverage?.toFixed(2)}</p>
                </div>
                <div>
                  <p className="text-sm text-muted-foreground mb-1">Your Responsibility</p>
                  <p className="text-2xl font-bold text-primary">${treatmentPlan?.patientResponsibility?.toFixed(2)}</p>
                </div>
              </div>
            </div>

            {/* Tabs */}
            <div className="bg-card rounded-xl shadow-elevation-2 border border-border mb-6">
              <div className="border-b border-border overflow-x-auto">
                <div className="flex">
                  {tabs?.map(tab => (
                    <button
                      key={tab?.id}
                      onClick={() => setActiveTab(tab?.id)}
                      className={`flex items-center gap-2 px-4 md:px-6 py-3 text-sm font-medium whitespace-nowrap transition-colors ${
                        activeTab === tab?.id
                          ? 'text-primary border-b-2 border-primary' : 'text-muted-foreground hover:text-foreground'
                      }`}
                    >
                      <Icon name={tab?.icon} size={18} />
                      {tab?.label}
                    </button>
                  ))}
                </div>
              </div>

              <div className="p-4 md:p-6">
                {activeTab === 'details' && (
                  <TreatmentPlanDetails treatmentPlan={treatmentPlan} />
                )}
                {activeTab === 'consent' && (
                  <ConsentWorkflow 
                    treatmentPlan={treatmentPlan}
                    onAcknowledgmentChange={loadTreatmentPlan}
                  />
                )}
                {activeTab === 'comparison' && (
                  <TreatmentComparison 
                    treatmentPlan={treatmentPlan}
                    alternatives={treatmentPlan?.alternatives || []}
                  />
                )}
                {activeTab === 'discussion' && (
                  <DiscussionPanel 
                    consentWorkflow={treatmentPlan?.consentWorkflow}
                    onRequestConsultation={handleRequestConsultation}
                  />
                )}
              </div>
            </div>

            {/* Action Buttons */}
            {treatmentPlan?.consentWorkflow?.consentStatus === 'pending' && (
              <div className="bg-card rounded-xl shadow-elevation-2 border border-border p-4 md:p-6">
                <div className="flex flex-col md:flex-row gap-3">
                  <Button 
                    variant="default" 
                    size="lg" 
                    iconName="CheckCircle"
                    onClick={handleAcceptPlan}
                    disabled={!allAcknowledged}
                    className="flex-1"
                  >
                    Accept Treatment Plan
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    iconName="MessageSquare"
                    onClick={() => setActiveTab('discussion')}
                    className="flex-1"
                  >
                    Discuss with Provider
                  </Button>
                  <Button 
                    variant="outline" 
                    size="lg" 
                    iconName="XCircle"
                    onClick={handleRejectPlan}
                  >
                    Decline
                  </Button>
                </div>
                {!allAcknowledged && (
                  <p className="text-sm text-warning mt-3 flex items-center gap-2">
                    <Icon name="AlertCircle" size={16} />
                    Please review and acknowledge all treatment phases in the Consent Workflow tab before accepting.
                  </p>
                )}
              </div>
            )}
          </div>
        </div>

        <QuickActions />
      </div>
      {/* Digital Signature Modal */}
      {showSignature && (
        <DigitalSignature 
          onComplete={handleSignatureComplete}
          onCancel={() => setShowSignature(false)}
          treatmentPlanName={treatmentPlan?.planName}
        />
      )}
    </div>
  );
};

export default TreatmentPlanReview;